import { useCallback, useEffect, useMemo, useState } from "react";
import { PAGE_PART } from "../settings/Page.jsx";
import Notice, { SaveStatus } from "../ui/Notice.jsx";
import { SkeletonRows } from "../ui/Skeleton.jsx";
import { useMyPermissions } from "../../lib/permissions.js";
import { denialOrRawMessage } from "../../lib/denialReason.js";
import { listDirectoryGroups } from "../../lib/identityClient.js";
import PaginatedPicker, { PickerRow } from "./PaginatedPicker.jsx";
import { Action, ProviderTag } from "./EscalationParts.jsx";
import {
    approverGroupKey,
    getEscalationApproverGroups,
    setEscalationApproverGroups,
} from "../../lib/escalationClient.js";

const PAGE_SIZE = 50;
const NOTE = "text-[12px] leading-[1.45] text-[color:var(--muted)]";
// How long "Approvers saved" stays beside the button.
const SAVED_MS = 2500;

export default function ApproverGroupsPanel() {
    const caps = useMyPermissions();
    const [saved, setSaved] = useState([]);
    const [draft, setDraft] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [adding, setAdding] = useState(false);
    const [justSaved, setJustSaved] = useState(false);
    useEffect(() => {
        if (!justSaved) return undefined;
        const timer = setTimeout(() => setJustSaved(false), SAVED_MS);
        return () => clearTimeout(timer);
    }, [justSaved]);

    // Errors are held raw and turned into copy at render, so the posture object
    // never becomes an effect dependency.
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        getEscalationApproverGroups()
            .then((groups) => {
                if (cancelled) return;
                setSaved(groups);
                setDraft(groups);
                setError(null);
            })
            .catch((err) => { if (!cancelled) setError(err); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    const savedKeys = useMemo(() => saved.map(approverGroupKey).join("|"), [saved]);
    const draftKeys = useMemo(() => draft.map(approverGroupKey).join("|"), [draft]);
    const dirty = savedKeys !== draftKeys;

    const save = useCallback(async () => {
        setSaving(true);
        setError(null);
        try {
            const groups = await setEscalationApproverGroups(draft);
            setSaved(groups);
            setDraft(groups);
            setAdding(false);
            setJustSaved(true);
        } catch (err) {
            setError(err);
        } finally {
            setSaving(false);
        }
    }, [draft]);

    const add = useCallback((group) => {
        setDraft((current) => (
            current.some((existing) => approverGroupKey(existing) === approverGroupKey(group))
                ? current
                : [...current, group]
        ));
        setAdding(false);
    }, []);

    return (
        <section className="grid max-w-[720px] gap-[14px]">
            <header className="grid gap-[4px]">
                <h3 className={PAGE_PART}>Who can approve requests</h3>
                <p className={`${NOTE} m-0`}>
                    Members of these groups can approve, deny, and revoke access.
                    Membership changes apply after directory sync.
                </p>
            </header>

            <div aria-busy={loading ? "true" : undefined} className="grid min-w-0 rounded-[10px] border border-[color:var(--line)]">
                {loading ? (
                    <div className="px-[12px]">
                        <SkeletonRows rows={2} cells={["w-[40%]"]} rowClassName="grid h-[52px] items-center last:border-b-0" label="Loading approver groups..." />
                    </div>
                ) : draft.length ? draft.map((group) => (
                    <div
                        key={approverGroupKey(group)}
                        className="flex min-w-0 items-center justify-between gap-[10px] border-b border-[color:var(--line-soft)] px-[12px] py-[10px] last:border-b-0"
                    >
                        <span className="grid min-w-0 gap-[2px]">
                            <span className="flex min-w-0 items-center gap-[6px]">
                                <span className="truncate text-[13px] leading-[1.3] font-medium text-[color:var(--ink)]">
                                    {group.displayName || "No longer in the directory"}
                                </span>
                                <ProviderTag provider={group.sourceProvider} />
                            </span>
                            <span className={`${NOTE} truncate`} title={group.sourceId}>
                                {group.sourceId}
                            </span>
                        </span>
                        <Action
                            onClick={() => setDraft((current) => current.filter(
                                (existing) => approverGroupKey(existing) !== approverGroupKey(group),
                            ))}
                        >
                            Remove
                        </Action>
                    </div>
                )) : (
                    <p className={`${NOTE} m-0 px-[12px] py-[12px]`}>
                        No approver groups. Only users with approval permission can approve requests.
                    </p>
                )}
            </div>

            {adding ? (
                <GroupPicker onAdd={add} onCancel={() => setAdding(false)} exclude={draft} />
            ) : (
                <div>
                    <Action onClick={() => setAdding(true)}>Add a group</Action>
                </div>
            )}

            {error ? <Notice tone="error">{denialOrRawMessage(error, caps)}</Notice> : null}

            <div className="flex min-h-[32px] items-center justify-end gap-[10px]">
                <SaveStatus status={justSaved && !dirty ? "saved" : null} savedLabel="Approvers saved" />
                {dirty ? (
                    <>
                        <Action onClick={() => { setDraft(saved); setError(null); }}>Discard</Action>
                        <Action tone="primary" disabled={saving} onClick={save}>
                            {saving ? "Saving..." : "Save approvers"}
                        </Action>
                    </>
                ) : null}
            </div>
        </section>
    );
}

function GroupPicker({ onAdd, onCancel, exclude }) {
    const caps = useMyPermissions();
    const excluded = useMemo(() => new Set(exclude.map(approverGroupKey)), [exclude]);

    const fetchPage = useCallback(async ({ search, cursor }) => {
        const page = await listDirectoryGroups({
            search,
            page_size: PAGE_SIZE,
            page_token: cursor ?? "",
        });
        return {
            rows: (page?.groups ?? []).map((group) => ({
                sourceProvider: group.sourceProvider ?? "",
                integrationId: group.integrationId ?? "",
                sourceId: group.sourceId ?? "",
                displayName: group.displayName || group.sourceId || "",
                memberCount: Number(group.memberCount ?? 0),
            })),
            total: page?.totalCount ?? null,
            nextCursor: page?.nextPageToken || null,
        };
    }, []);

    return (
        <div className="grid gap-[8px] rounded-[10px] border border-[color:var(--line)] p-[12px]">
            <PaginatedPicker
                autoFocus
                placeholder="Search directory groups"
                ariaLabel="Search directory groups"
                emptyText="No groups match that search."
                errorText={(err) => denialOrRawMessage(err, caps)}
                fetchPage={fetchPage}
                renderRow={(group) => {
                    const key = approverGroupKey(group);
                    const already = excluded.has(key);
                    return (
                        <PickerRow
                            key={key}
                            primary={group.displayName}
                            secondary={already ? "already added" : `${group.memberCount.toLocaleString()} members`}
                            badge={<ProviderTag provider={group.sourceProvider} />}
                            selected={already}
                            onSelect={() => { if (!already) onAdd(group); }}
                        />
                    );
                }}
            />
            <div className="flex justify-end">
                <Action onClick={onCancel}>Cancel</Action>
            </div>
        </div>
    );
}
